import {
  TValueManagers,
  TValueRights,
} from '@app/services/rights-service/response-interfaces/get-rights.response';
import { AppTable } from '@app/shared/components/AppTable';
import { roleBasedRouteContext } from '@app/shared/components/RoleBasedRoute/roleBasedRouteContext';
import { useAccountRoleRights } from '@app/shared/hooks/useAccountRoleRights';
import { NO_TABLE_DATA_TEXT } from '@app/shared/texts/texts';
import {
  deleteValueRightsAction,
  forceSaveValueRightsAction,
  selectValueData,
  selectValueLoading,
  selectValueTotal,
  selectValueUpdateData,
} from '@app/store/settings/value-rights';
import Switch from '@material-ui/core/Switch';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  sortGroup1,
  sortGroup2,
  sortGroup3,
  sortGroup4,
  sortUserName,
} from '../../../utils/ProductValueHelperSort';
import { makeStyles, Theme } from '@material-ui/core';
import { createStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    disableSwitch: {
      '&.Mui-checked.Mui-disabled': {
        color: theme.palette.primary.main,
        '& + .MuiSwitch-track': {
          backgroundColor: theme.palette.primary.main,
        },
      },
    },
  })
);

export const ValueRightsTable: React.FC = () => {
  //===== style =====
  const classes = useStyles();

  //=====  =====
  const ctx = React.useContext(roleBasedRouteContext);
  const roleHooks = useAccountRoleRights(ctx.feature);
  const canEdit = roleHooks[1];
  const isDisabled = canEdit() ? false : true;

  const dispatch = useDispatch();
  const data = useSelector(selectValueData);
  const updateData = useSelector(selectValueUpdateData);
  const total = useSelector(selectValueTotal);
  const loading = useSelector(selectValueLoading);

  const [currentManager, setCurrentManager] = React.useState<
    Record<string, TValueManagers>
  >();
  React.useEffect(() => {
    const currentManagers = data.map((i) => {
      let find = i.managers.find((f) => f.isPrimaryProductOwner === true);

      if (!find) {
        find = i.managers[0];
      }

      return {
        id: i.productGroupId,
        manager: find,
      };
    });
    const dataToManagers = currentManagers.reduce((accum, item) => {
      return {
        ...accum,
        [item.id]: item.manager as TValueManagers,
      };
    }, {} as Record<string, TValueManagers>);
    setCurrentManager(dataToManagers);
  }, [data]);
  const dataTable = React.useMemo(() => {
    return data.map((i) => {
      const name =
        currentManager && currentManager[i.productGroupId]
          ? currentManager[i.productGroupId].name
          : 'a';
      if (updateData[i.productGroupId]) {
        return {
          ...i,
          userName: name,
          managers: i.managers,
        };
      }
      return { ...i, userName: name };
    });
  }, [updateData, data, currentManager]);
  const handleAutoComplete = React.useCallback(
    (value: TValueManagers, item: TValueRights) => {
      setCurrentManager((state) => {
        return {
          ...state,
          [item.productGroupId]: value,
        };
      });
    },
    []
  );

  const handleSwitcher = React.useCallback(
    (e: any, item: TValueRights) => {
      const value = e.target.checked;
      let data = { ...updateData };
      if (updateData[item.productGroupId]) {
        const managers = data[item.productGroupId].managers;
        managers.forEach((i: TValueManagers) => {
          let manager = currentManager as Record<string, TValueManagers>;
          if (i.id === manager[item.productGroupId].id) {
            i.isPrimaryProductOwner = value;
            return;
          }
          i.isPrimaryProductOwner = false;
        });
        data = {
          ...data,
          [item.productGroupId]: {
            managers,
          },
        };
      }
      if (!updateData[item.productGroupId]) {
        const currentGroup = dataTable.find(
          (f) => f.productGroupId === item.productGroupId
        );
        let managers: TValueManagers[] = [];
        if (currentGroup) {
          managers = [...currentGroup.managers];
        }
        managers.forEach((i) => {
          let manager = currentManager as Record<string, TValueManagers>;
          if (i.id === manager[item.productGroupId].id) {
            i.isPrimaryProductOwner = value;
            return;
          }
          i.isPrimaryProductOwner = false;
        });
        data = {
          ...data,
          [item.productGroupId]: {
            managers,
          },
        };
      }
      dispatch(forceSaveValueRightsAction({ items: data }));
    },
    [updateData, dispatch, currentManager, dataTable]
  );
  const handleDelete = React.useCallback(
    (items: TValueRights[]) => {
      const dto = items.map((i) => {
        const managers: Record<string, TValueManagers> = {
          ...currentManager,
        };
        return {
          userId: managers[i.productGroupId].id,
          productGroupId: i.productGroupId,
        };
      });
      dispatch(
        deleteValueRightsAction({
          items: dto as {
            userId: string;
            productGroupId: string;
          }[],
        })
      );
    },
    [dispatch, currentManager]
  );
  return (
    <AppTable
      data={dataTable}
      dataCount={total}
      onDelete={handleDelete}
      deletableItems={!dataTable.length ? false : true}
      loading={loading}
      isEmpty={!dataTable.length}
    >
      <AppTable.Head>
        <AppTable.Row isHead={true} disableDeletionBehavior={true}>
          <AppTable.HeadCell sortHandler={sortUserName}>
            User name
          </AppTable.HeadCell>
          <AppTable.HeadCell sortHandler={sortGroup1}>
            Product Group 1
          </AppTable.HeadCell>
          <AppTable.HeadCell sortHandler={sortGroup2}>
            Product Group 2
          </AppTable.HeadCell>
          <AppTable.HeadCell sortHandler={sortGroup3}>
            Product Group 3
          </AppTable.HeadCell>
          <AppTable.HeadCell sortHandler={sortGroup4}>
            Product Group 4
          </AppTable.HeadCell>
          <AppTable.HeadCell>Primary label product owner</AppTable.HeadCell>
        </AppTable.Row>
      </AppTable.Head>
      <AppTable.Body<TValueRights>
        contentColSpan={6}
        noDataText={NO_TABLE_DATA_TEXT}
      >
        {(items) =>
          items.map((i) => {
            return (
              <AppTable.Row key={i.productGroupId} item={i}>
                <AppTable.Cell>
                  {currentManager &&
                  currentManager[i.productGroupId] &&
                  Object.keys(currentManager).length ? (
                    <Autocomplete
                      disableClearable={true}
                      options={i.managers}
                      getOptionLabel={(option) => `${option.name}`}
                      onChange={(_e, value) => handleAutoComplete(value, i)}
                      value={currentManager[i.productGroupId]}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="User Name"
                          variant="outlined"
                          size="small"
                        />
                      )}
                    />

                  ) : null}
                </AppTable.Cell>
                <AppTable.Cell>{i.groupLevelOneName}</AppTable.Cell>
                <AppTable.Cell>{i.groupLevelTwoName}</AppTable.Cell>
                <AppTable.Cell>{i.groupLevelThreeName}</AppTable.Cell>
                <AppTable.Cell>{i.groupLevelFourName}</AppTable.Cell>
                <AppTable.Cell>
                  {currentManager &&
                  currentManager[i.productGroupId] &&
                  Object.keys(currentManager).length ? (
                    <Switch
                      onChange={(e: any) => handleSwitcher(e, i)}
                      checked={
                        currentManager[i.productGroupId].isPrimaryProductOwner
                      }
                      color="primary"
                      disabled={
                        isDisabled ||
                        currentManager[i.productGroupId].isPrimaryProductOwner
                      }
                      classes={{ disabled: classes.disableSwitch }}
                    />
                  ) : null}
                </AppTable.Cell>
              </AppTable.Row>
            );
          })
        }
      </AppTable.Body>
    </AppTable>
  );
};
